import React, { FC, useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  TooltipItem
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import SwitchSelector from 'react-switch-selector';

import HeaderBtn from '../../headerBtn/headerBtn';

import { ReactComponent as SettingIcon } from '../../../assets/icons/settings.svg';
import { ReactComponent as IncreaseIcon } from '../../../assets/icons/increase.svg';
import { ReactComponent as ReduceIcon } from '../../../assets/icons/reduce.svg';
import { ReactComponent as ArrowIcon } from '../../../assets/icons/chevron.svg';
import { ReactComponent as FiltersIcon } from '../../../assets/icons/filter.svg';
import { ReactComponent as ActiveFiltersIcon } from '../../../assets/icons/filter_active.svg';

import { AppDispatch } from "../../../redux/reducer/store";
import { actionsAnalyticActions } from '../../../redux/action/charts/actionCharts';
import { reducerChangeDataCharts, reducerEditTypeChart, reducerChartsFilters } from '../../../redux/reducer/charts/reducer/reducerCharts';

import ChartComponent from '../../custom/chartComponet/chartComponent';
import ChartLoader from '../../custom/chartLoader/chartLoader';
import ScrollingLegend from '../../custom/legend/legend';
import {
  createFormatDateFunction,
  monthOptions,
  defaultFilter,
  lineChartList,
  filteredZeroData,
  reverseChartsList,
  tickData,
  getColorOptions,
  adjustValuesToHundred
} from '../../../utils/chartsData';
import { formatDateForMapReport } from '../../../utils/formatDate';
import { IChartOptions, IFiltersCharts, ICircleDataset, IDataChartOptions, IContextOptions } from '../../../types/chartsInterface';

import "../charts.scss";

interface IDesktopData {
  id: number;
  actionsCharts: any[];
  periodList: string[];
  labels: string[] | number[];
  backgroundColorOptions: string[];
  dataCircle: ICircleDataset;
  dataActionCharts: IDataChartOptions[];
  dataUnitedHotlines: IDataChartOptions[];
  dataCircleHotlines: ICircleDataset;
};

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  ChartDataLabels,
  PointElement,
  LineElement
);

const switchOptoins = [
  { value: 0, label: 'Разъединить' },
  { value: 1, label: 'Объединить' },
];

const typesCharts = [
  { value: 'barStackedNorm', label: 'Нормированная гистограмма с накоплением' },
  { value: 'lineBarStacked', label: 'Нормированная линейчатая с накоплением' },
  { value: 'lineChartStackedNorm', label: 'Нормированный график с накоплением' },
  { value: 'pie', label: 'Круговая диаграмма' },
  { value: 'doughnut', label: 'Кольцевая диаграмма' },
];

const actionsDefaultFiiters = {
  ...defaultFilter,
  group: { value: '6', label: 'По сети' }
};

const ActionsChart: FC<IChartOptions> = ({ id, copyChart, isCopy, pageId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const listRef = useRef<HTMLDivElement | null>(null);
  const legendRef = useRef<HTMLDivElement>(null);
  const scrollStep = 21.5;

  const {
    pending,
    desktopData,
    settingCharts,
    desktopInputValues,
  } = useSelector((state: any) => state.charts?.chartsList);

  const {
    filters,
    areasOptions,
    chainsOptions,
    regionsOptions,
    projectOptions,
    contractsOptions,
  } = useSelector((state: any) => state.userReports.userReports);
  const { darkTheme } = useSelector((state: any) => state.theme.theme);

  const ticks = {
    ...tickData,
    color: darkTheme.isDarkTheme ? 'white' : 'black',
  };
  const colorList = getColorOptions(settingCharts?.actions);
  const actionsFilters = desktopInputValues?.[pageId]?.[id] || null;
  const index = desktopData[pageId || 0]?.find((el: IDesktopData) => el.id === id) || {};

  const {
    type,
    labels,
    dataCircle,
    periodList,
    dataUnitedHotlines,
    actionsCharts,
    dataActionCharts,
    dataCircleHotlines,
  } = index;

  const [isIncreasedSize, setIsIncreasedSize] = useState<boolean>(false);
  const [isOpenSetting, setIsOpenSetting] = useState<boolean>(false);
  const [isOpenSectionCharts, setIsOpenSectionCharts] = useState<boolean>(false);
  const [isListRightPosition, setIsListRightPosition] = useState<boolean>(false);
  const [isHiddenLabel, setIsHiddenLabel] = useState<string[]>([]);
  const [isListRightFiltersPosition, setIsListRightFiltersPosition] = useState<boolean>(false);
  const [isChangeData, setIsChangeData] = useState<boolean>();
  const [isVisiblelabel, setIsVisiblelabel] = useState<any[]>([]);
  const [isloader, setIsloader] = useState<boolean>(true);
  const [isPeriod, setIsPeriod] = useState<string[]>([]);
  const [isOn, setIsOn] = useState<number>(0);
  const [isData, setIsData] = useState<any>([]);

  const typeCircle = type === 'pie' || type === 'doughnut';
  const initialSelectedIndex = switchOptoins.findIndex(option => option.value === isOn);

  const axisX = [
    {
      formatDate: (data: string) => {
        return formatDateForMapReport(data);
      }
    },
    {
      formatDate: (el: string) => {
        const parts = el.split('W');
        return parseInt(parts[1], 10);
      }
    },
    {
      formatDate: createFormatDateFunction(monthOptions)
    },
    {
      formatDate: (el: string) => { return el }
    },
    {
      formatDate: createFormatDateFunction(contractsOptions)
    },
    {
      formatDate: createFormatDateFunction(projectOptions)
    },
    {
      formatDate: createFormatDateFunction(chainsOptions)
    },
    {
      formatDate: createFormatDateFunction(areasOptions)
    },
    {
      formatDate: createFormatDateFunction(regionsOptions)
    },
    {
      formatDate: (el: string) => { return el }
    },
  ];

  const data = {
    labels: periodList ? isPeriod : [],
    datasets: isData,
  };

  const optionsForBar = {
    responsive: true,
    indexAxis: type === 'lineBarStacked'
      ? 'y' as const
      : 'x' as const,
    layout: {
      padding: {
        top: 30,
        right: 0,
        left: 0
      }
    },
    scales: {
      x: {
        stacked: true,
        max: 100,
        ticks: ticks,
        grid: {
          color: darkTheme.isDarkTheme ? 'white' : 'black',
        }
      },
      y: {
        stacked: true,
        max: 100,
        ticks: ticks,
        grid: {
          color: darkTheme.isDarkTheme ? 'white' : 'black',
        }
      }
    },
    plugins: {
      tooltip: {
        boxWidth: 10,
        position: 'nearest' as 'nearest',
        callbacks: {
          title: (tooltipItems: TooltipItem<typeof type>[]) => {
            const ind = tooltipItems[0].dataIndex;
            const label = tooltipItems[0].dataset.additionalData ? tooltipItems[0].dataset.additionalData[ind] : tooltipItems[0].label;

            function splitStringIntoChunks(str: string) {
              return str.match(new RegExp('.{1,' + 91 + '}', 'g')) || [];
            };

            const newLabel = label?.length && label?.length < 100
              ? axisX[actionsFilters?.group?.value].formatDate(label) || label
              : splitStringIntoChunks(label);
            return newLabel;
          },
          label: (tooltipItems: TooltipItem<typeof type>) => {
            return `${tooltipItems.dataset.label} ${tooltipItems.formattedValue}%`
          }
        },
      },
      legend: {
        display: false,
      },
      datalabels: {
        color: '#000',
        align: 'center' as const,
        anchor: 'center' as const,
        font: {
          size: 11,
        },
        formatter: (value: number, context: IContextOptions) => {
          if (value > 3 && !lineChartList.includes(type)) {
            const { chart } = context;
            const datasets = chart.data.datasets;
            const dataIndex = context.dataIndex;
            const datasetIndex = context.datasetIndex;
            const valuesInColumn = datasets.map((dataset: any) => dataset.data[dataIndex]);
            const newValue = adjustValuesToHundred(valuesInColumn)[datasetIndex];

            const animation = chart.getDatasetMeta(context.datasetIndex).data[context.dataIndex]?.$animations || null;
            const barWidth = animation ? animation?.width?._to : 1;
            const barHeigth = animation ? animation?.height?._to : 1;
            const widthSize = (newValue.toString().length + 1) * 5;

            return barHeigth > 13 && barWidth >= widthSize ? `${newValue}%` : '';
          } else {
            return '';
          }
        },
        clip: false,
        display: true,
        size: 1,
      },
    },
  };

  const optionsForCircle = {
    responsive: true,
    indexAxis: 'x' as const,
    layout: {
      padding: {
        bottom: 10,
        top: -10,
      }
    },
    plugins: {
      tooltip: {
        boxWidth: 10,
        position: 'nearest' as 'nearest',
        callbacks: {
          title: (tooltipItem: TooltipItem<typeof type>[]) => {
            const dataset = tooltipItem[0].dataset;
            const label = dataset?.labels ? dataset.labels[tooltipItem[0].dataIndex] : '';

            function splitStringIntoChunks(str: string) {
              return str ? str.match(new RegExp('.{1,' + 51 + '}', 'g')) : [];
            };

            const newLabel = label?.length && label.length < 60
              ? axisX[actionsFilters?.group?.value].formatDate(label) || label
              : splitStringIntoChunks(label);

            return `${newLabel}`
          },
          label: function (tooltipItem: TooltipItem<typeof type>) {
            return `${tooltipItem.dataset.data[tooltipItem.dataIndex].toFixed(2)}%`;
          }
        }
      },
      legend: {
        display: false,
      },
      datalabels: {
        anchor: 'center' as const,
        align: 'center' as const,
        formatter: (value: number) => {
          return value < 3 ? '' : value.toFixed() + '%';
        },
        color: '#000',
        offset: isIncreasedSize || type === 'pie'
          ? -40 : -13,
        display: true,
      },
    },
  };

  const actualOptionsChart = typeCircle ? optionsForCircle : optionsForBar;

  const getChangeData = (data: IFiltersCharts) => {
    const changes: IFiltersCharts = {};

    for (const key in actionsDefaultFiiters) {
      if (actionsDefaultFiiters.hasOwnProperty(key)) {
        // @ts-ignore
        if (JSON.stringify(actionsDefaultFiiters[key]) !== JSON.stringify(data[key])) {
          // @ts-ignore
          changes[key] = data[key];
        }
      }
    }

    for (const key in data) {
      if (data.hasOwnProperty(key) && !actionsDefaultFiiters.hasOwnProperty(key) && key !== 'status') {
        // @ts-ignore
        changes[key] = data[key];
      }
    };
    setIsChangeData(Object.keys(changes).length > 0);
    dispatch(reducerChangeDataCharts({ hasChanged: Object.keys(changes).length > 0, changes }));
  };

  const handleClickSetting = () => {
    if (isOpenSetting) {
      setIsOpenSectionCharts(false);
    } else {
      getChangeData(actionsFilters);
    }
    setIsOpenSetting(!isOpenSetting);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (listRef.current && !listRef.current.contains(event.target as Node)) {
      setIsOpenSetting(false);
      setIsOpenSectionCharts(false);
    }
  };

  const handleClickOnLegend = (name: string) => {
    if (!isHiddenLabel.includes(name)) {
      setIsHiddenLabel((prevItems: string[]) => {
        return [...prevItems, name];
      });
    } else {
      setIsHiddenLabel((prevItems: string[]) => {
        return prevItems.filter(item => item !== name);
      });
    }
  };

  const handleClickFilterOnType = (name: string, index: number) => {
    setIsOn(0);
    dispatch(reducerEditTypeChart({ id, pageId, data: name }));
    setIsHiddenLabel([]);
  };

  const handleChangeSwitchSelector = (newValue: any) => {
    setIsOn(newValue);
    dispatch(reducerChartsFilters({
      pageId,
      id, data:
      {
        ...actionsFilters,
        status: newValue
      }
    }));
  };

  useEffect(() => {
    if (actionsFilters) {
      getChangeData(actionsFilters);
    }
    // eslint-disable-next-line
  }, [actionsFilters])

  useEffect(() => {
    if (isPeriod.length > 0) {
      const timeoutId = setTimeout(() => {
        const updatedData = typeCircle
          ? isOn ? [dataCircleHotlines] : [dataCircle]
          : !dataActionCharts ? [] : isOn ? dataUnitedHotlines : dataActionCharts;
        const directionData = reverseChartsList.includes(type) ? updatedData.slice().reverse() : updatedData;
        setIsVisiblelabel(typeCircle ? [...labels, 'Не стартовала'] : filteredZeroData(updatedData));
        setIsData(directionData);
      }, 500);

      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line
  }, [isPeriod, type, isOn, dataActionCharts]);

  useEffect(() => {
    const period = actionsFilters && actionsCharts ? periodList.map((el: string) => {
      return axisX[actionsFilters?.group?.value].formatDate(el);
    }) : [];

    setIsPeriod(period);
    // eslint-disable-next-line
  }, [periodList])

  useEffect(() => {
    if (isOpenSetting) {
      const listEl = listRef.current;
      if (listEl) {
        const list = listEl.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        setIsListRightPosition(list.right + 168 > viewportWidth);
        setIsListRightFiltersPosition(list.right + 505 > viewportWidth);
      }
    }
    // eslint-disable-next-line
  }, [isOpenSetting]);

  useEffect(() => {
    if (filters) {
      setIsloader(true);
      dispatch(actionsAnalyticActions(id, pageId)).then(() => {
        setIsloader(false);
      });
    }
    setIsOn(actionsFilters?.status || 0);
    // eslint-disable-next-line
  }, [filters]);

  useEffect(() => {
    if (pending[pageId || 0]) {
      setIsloader(pending[pageId]?.includes(id) ? true : false);
    }
    // eslint-disable-next-line
  }, [pending]);

  useEffect(() => {
    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();

      if (legendRef.current) {
        const currentScrollTop = legendRef.current.scrollTop;
        const newScrollTop =
          event.deltaY > 0
            ? currentScrollTop + scrollStep
            : currentScrollTop - scrollStep;

        legendRef.current.scrollTo({
          top: newScrollTop,
          behavior: 'smooth',
        });
      }
    };

    const legendElement = legendRef.current;

    if (legendElement) {
      legendElement.addEventListener('wheel', handleWheel, { passive: false });
    }

    return () => {
      if (legendElement) {
        legendElement.removeEventListener('wheel', handleWheel);
      }
    };
  }, [scrollStep, isloader]);

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setIsloader(true);
    dispatch(actionsAnalyticActions(id, pageId)).then(() => {
      setIsloader(false);
    });
    // eslint-disable-next-line
  }, []);

  return (
    <div className={`content-charts ${isIncreasedSize ? 'content-charts_active' : ''}`}>
      <div className='charts__header'>
        <p className='charts__title'>{`Акции: стартовала/не стартовала ${isCopy ? '(дубль)' : ''}`}</p>
        <div className='switchContainer switchContainer_precent' style={{ width: '195px' }}>
          <SwitchSelector
            options={switchOptoins}
            onChange={handleChangeSwitchSelector}
            forcedSelectedIndex={initialSelectedIndex}
            fontColor={"#000"}
            name={`checkStatus_${id}`}
          />
        </div>
        <div className='charts__btn'>
          <div>
            {!isIncreasedSize && <IncreaseIcon className='charts__icon' onClick={() => setIsIncreasedSize(true)} />}
            {isIncreasedSize && <ReduceIcon className='charts__icon' onClick={() => setIsIncreasedSize(false)} />}
          </div>
          <div className='charts__headerbtn'>
            <HeaderBtn
              content=" "
              purpose="filtersCharts"
              modalTitle="Настройка отображения данных"
              selectId={id}
              action={actionsAnalyticActions}
              inputValuesCharts={actionsFilters}
            />
            {(!isChangeData || !actionsFilters) &&
              <FiltersIcon
                className={`charts__icon charts__icon_filters`}
                onClick={handleClickSetting} />}
            {isChangeData && actionsFilters &&
              <ActiveFiltersIcon
                className={`charts__icon charts__icon_filters`}
                onClick={handleClickSetting} />}
          </div>
          <div ref={listRef}>
            <SettingIcon className='charts__icon' onClick={handleClickSetting} />
            {isOpenSetting &&
              <ul className={`charts__setting ${isListRightPosition ? 'charts__setting_right' : ''}`}>
                <li onClick={() => {
                  handleClickSetting();
                  copyChart(id);
                }}>Дублировать</li>
                <li>
                  <HeaderBtn
                    content="Удалить"
                    purpose="removeCharts"
                    modalTitle="Удалить диаграмму?"
                    selectId={id} />
                </li>
                <li
                  style={{ position: 'relative' }}
                  onClick={() => {
                    setIsOpenSectionCharts(!isOpenSectionCharts);
                  }}
                >Тип диаграммы <ArrowIcon className='charts__icon' />
                  {isOpenSectionCharts &&
                    <ul className={`charts__setting charts__setting_list ${isListRightFiltersPosition ? 'charts__setting_list_typeRight' : ''}`}>
                      {typesCharts.map((el: { value: string, label: string }, index: number) => (
                        <li
                          key={index}
                          className={type === el.value ? 'item_active' : ''}
                          onClick={() => handleClickFilterOnType(el.value, index)}
                        >{el.label}</li>
                      ))}
                    </ul>}
                </li>
                <li>
                  <HeaderBtn
                    content="Скриншот"
                    purpose="screenCharts"
                    modalTitle="Сделать скриншот"
                    dataCharts={{
                      data,
                      isOn,
                      name: `Акции: стартовала/не стартовала ${isCopy ? '(дубль)' : ''}`,
                      type: type,
                      options: actualOptionsChart,
                      isIncreasedSize,
                      labels: isOn ? ['Стартовала', 'Не стартовала'] : labels,
                      isHiddenItem: isHiddenLabel,
                      colorOptions: colorList,
                      chartType: 'actions'
                    }} />
                </li>
                <li>
                  <HeaderBtn
                    content="Настройка цвета"
                    purpose="changeColor"
                    modalTitle="Настройка цвета"
                    dataCharts={{ type: "actions", labels: !isOn ? labels : ['Стартовала', 'Не стартовала'], isVisiblelabel }}
                  />
                </li>
                {/* <li onClick={handleClickSetting}>Выгрузить данные</li> */}
              </ul>}
          </div>
        </div>
      </div>

      {isloader ?
        <ChartLoader />
        : actionsCharts?.length === 0
          ? <p className='charts__text'>Данные по акциям в выбранных вами отчётах не найдены. Убедитесь, что вы заказали эту услугу, расширьте рассматриваемый период  или географический охват.
          </p>
          : <>
            <ChartComponent
              type={type}
              data={data}
              isHiddenItem={isHiddenLabel}
              options={actualOptionsChart}
              isIncreasedSize={isIncreasedSize}
              loader={isloader}
              chartType={'actions'}
              colorOptions={colorList}
            />
            <ScrollingLegend
              colorOptions={colorList}
              isIncreasedSize={isIncreasedSize}
              isLabels={!isOn ? labels : ['Стартовала', 'Не стартовала']}
              isVisiblelabel={isVisiblelabel}
              handleClickOnLegend={handleClickOnLegend}
              isHiddenLabel={isHiddenLabel}
              type={type}
            />
          </>
      }
    </div>
  )
};

export default ActionsChart;